import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { SalottoIntro } from "../components/SalottoIntro";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { GUIDES, TOOLS, IMAGES } from "../lib/brand";
import { setSeo } from "../lib/seo";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Safe, verified-only description for the Collection listing (deliverables not advertised).
const COURSE_SAFE_DESC = "A practical introduction to Estate Management for those who don't know where to begin — combining professional context, a clear roadmap and practical starter resources for taking the next steps toward the profession.";

const NOTES_COPY = "Thoughts, practical lessons and observations from the world of Estate Management — delivered occasionally.";

const CATS = [
  { key: "begin", n: "01", label: "Begin", desc: "For those discovering Estate Management, or considering entering the profession." },
  { key: "learn", n: "02", label: "Learn", desc: "Deeper education for developing professional Estate Management knowledge." },
  { key: "use", n: "03", label: "Use", desc: "Practical resources designed to be used in the work — checklists, templates and tools." },
];

export default function Salotto() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const catParam = searchParams.get("c");
  const validCat = CATS.some((c) => c.key === catParam) ? catParam : "all";
  const [live, setLive] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState(validCat);
  const [checklist, setChecklist] = useState({ first_name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [checklistDone, setChecklistDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => { setSeo({ title: "Il Salotto Digitale — Estate Management courses, guides & tools · Marella Bolognesi", description: "Il Salotto Digitale is Marella Bolognesi's curated collection of Estate Management courses, guides and tools — for those entering and growing within private service." }); }, []);
  useEffect(() => { api.get("/courses").then((r) => setLive(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user) api.get("/my/courses").then((r) => setEnrolledIds(r.data.map((c) => c.id))).catch(() => {});
    else setEnrolledIds([]);
  }, [user]);

  // Arriving with a category (?c=begin|learn|use) filters and reveals the collection.
  useEffect(() => {
    setFilter(validCat);
    if (catParam && validCat !== "all") {
      const t = setTimeout(() => document.getElementById("collection")?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
      return () => clearTimeout(t);
    }
  }, [catParam, validCat]);

  const flagship = live.find((c) => c.id === "how-to-start-as-an-estate-manager") || live[0];

  const P2_ID = "becoming-an-estate-manager";
  const P2_DESC = "A guided, structured course through two professional pathways — preparing to enter Estate Management, and leading through the first ninety days inside a household.";
  const RSR_ID = "residence-service-record";
  const RSR_DESC = "A professional system for documenting service history, repairs, warranties, follow-up and preventive maintenance across a private residence.";

  const items = useMemo(() => {
    const courses = live.map((c) => {
      const isP2 = c.id === P2_ID, isRSR = c.id === RSR_ID;
      return {
        kind: "course", id: c.id, title: c.title,
        desc: isP2 ? P2_DESC : isRSR ? RSR_DESC : COURSE_SAFE_DESC,
        price: c.price,
        cover: isP2 ? (c.cover_url || "/becoming-hero.webp") : isRSR ? (c.cover_url || "/rsr-hero.webp") : c.cover_url,
        category: isP2 ? "learn" : isRSR ? "use" : "begin",
        available: c.available !== false,
        link: isP2 ? "/salotto-digitale/becoming" : isRSR ? "/salotto-digitale/residence-service-record" : null,
        linkLabel: isRSR ? "Get the Tool" : "Explore the Course",
        label: isRSR ? "Use · Professional Tool" : null,
        openPath: isP2 ? "/becoming" : isRSR ? "/tool/residence-service-record" : null,
      };
    });
    const guides = GUIDES.map((g) => ({ kind: "guide", id: g.id, title: g.title, desc: g.desc, priceLabel: "Complimentary", category: "begin", cover: IMAGES.mission, status: g.status }));
    const resources = TOOLS.map((t) => ({ kind: "resource", id: t.id, title: t.title, desc: t.desc, priceLabel: "Complimentary", category: "use", fileId: t.fileId, cover: IMAGES.texture }));
    return [...guides, ...courses, ...resources];
  }, [live]);

  const shown = filter === "all" ? items : items.filter((i) => i.category === filter);

  const enroll = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/payments/checkout", { course_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(null); }
  };

  const requestChecklist = async (e) => {
    e.preventDefault();
    if (!checklist.first_name || !checklist.email) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/checklist/request", checklist);
      const url = `${BACKEND}/api/checklist/download?token=${data.token}`;
      setDownloadUrl(url);
      window.open(url, "_blank");
      setChecklistDone(true);
    } catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };

  const goToCapture = () => document.getElementById("free")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const ItemRow = ({ it }) => {
    const enrolled = it.kind === "course" && enrolledIds.includes(it.id);
    return (
      <div data-testid={`library-item-${it.id}`} className="group grid grid-cols-12 gap-5 lg:gap-8 items-center py-8 border-b border-line">
        <div className="col-span-3 lg:col-span-2 overflow-hidden aspect-[4/5]">
          {it.cover ? <img src={it.cover} alt={it.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" /> : <div className="w-full h-full bg-surface" />}
        </div>
        <div className="col-span-9 lg:col-span-7">
          <p className="text-[10px] uppercase tracking-[0.2em] text-accent">{it.label ? it.label : it.category === "use" ? "Resource" : it.kind === "course" ? "Course" : "Guide"}</p>
          <h3 className="mt-2 font-serif font-light text-2xl lg:text-3xl text-ink leading-snug">{it.title}</h3>
          <p className="mt-2 text-sm text-ink-soft font-light leading-relaxed max-w-xl">{it.desc}</p>
        </div>
        <div className="col-span-12 lg:col-span-3 flex lg:flex-col lg:items-end gap-4 lg:gap-3 lg:text-right">
          <span className="font-serif text-2xl text-ink">{it.kind === "course" ? `$${Number(it.price).toFixed(0)}` : it.priceLabel}</span>
          {it.kind === "course" ? (
            enrolled ? (
              <button data-testid={`open-${it.id}`} onClick={() => nav(it.openPath || `/learn/${it.id}`)} className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">Go to library <ArrowRight size={13} /></button>
            ) : it.available === false ? (
              <span data-testid={`course-soon-${it.id}`} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft cursor-default select-none">Coming Soon</span>
            ) : it.link ? (
              <Link to={it.link} data-testid={`explore-${it.id}`} className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">{it.linkLabel || "Explore the Course"} <ArrowRight size={13} /></Link>
            ) : (
              <button data-testid={`enroll-${it.id}`} onClick={() => nav(`/course/${it.id}`)} className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">Explore the Course <ArrowRight size={13} /></button>
            )
          ) : it.kind === "resource" ? (
            <button data-testid={`get-${it.id}`} onClick={goToCapture} className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">Get the Checklist <ArrowRight size={13} /></button>
          ) : (
            it.status === "soon" ? (
              <span data-testid={`guide-soon-${it.id}`} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Coming Soon</span>
            ) : (
              <button data-testid={`guide-${it.id}`} onClick={() => nav("/guide/is-estate-management-for-me")} className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">Get the Guide <ArrowRight size={13} /></button>
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="salotto-page" className="pt-20">
      <SalottoIntro />
      {/* Opening */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src={IMAGES.texture} alt="" className="absolute right-0 top-0 h-full w-full lg:w-2/5 object-cover opacity-[0.22]" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/90 to-bg/40" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12 pt-20 lg:pt-28 pb-16">
          <FadeIn><MetaLabel>Il Salotto Digitale</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-8 font-serif font-light text-ink text-4xl sm:text-5xl lg:text-[5rem] leading-[1.02] tracking-tight max-w-4xl">
              A curated collection for those who run a private home — and those learning to.
            </h1>
          </FadeIn>
          <FadeIn delay={0.16}>
            <p className="mt-8 max-w-xl text-lg font-light text-ink-soft leading-relaxed">
              Courses, practical tools, guides and resources for those entering — or already working within — the world of Estate Management and private service.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Editorial introduction from Marella */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-start border-t border-line pt-16">
          <Reveal className="lg:col-span-3"><MetaLabel>From Marella</MetaLabel></Reveal>
          <div className="lg:col-span-9 max-w-3xl space-y-6 text-lg font-light text-ink-soft leading-relaxed">
            <Reveal><p className="font-serif text-2xl lg:text-3xl text-ink leading-[1.3]">Il Salotto Digitale grew from my own experience working inside private homes — and from realizing how difficult it can be to find practical, honest information about this profession.</p></Reveal>
            <Reveal delay={0.08}><p>Here, I share what I have learned through the work itself: how to begin, how to think, what to prepare for, and the systems that keep a home running well.</p></Reveal>
          </div>
        </div>
      </section>

      {/* Three categories */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid md:grid-cols-3 border-t border-line">
            {CATS.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08} className="border-b md:border-b-0 md:border-r last:border-r-0 border-line py-8 md:py-2 md:px-8 first:md:pl-0">
                <span className="font-serif text-accent text-xl">{c.n}</span>
                <h2 className="mt-3 font-serif font-light text-3xl lg:text-4xl text-ink">{c.label}</h2>
                <p className="mt-4 text-sm text-ink-soft font-light leading-relaxed max-w-xs">{c.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured flagship */}
      {flagship && (
        <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28">
          <Reveal><MetaLabel className="mb-10">The Featured Course</MetaLabel></Reveal>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <Reveal className="lg:col-span-5">
              <div className="overflow-hidden aspect-[4/5]">
                <img src={flagship.cover_url || IMAGES.livingWell} alt={flagship.title} className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <Reveal><p className="text-[11px] uppercase tracking-ultra text-accent">How to Start · $149</p></Reveal>
              <Reveal delay={0.05}><h2 className="mt-6 font-serif font-light text-4xl lg:text-6xl text-ink leading-[1.02] tracking-tight">{flagship.title}</h2></Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 font-serif italic text-xl text-ink-soft">You know you're interested in Estate Management. You just don't know where to begin.</p>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="mt-6 text-lg font-light text-ink-soft leading-relaxed max-w-xl">
                  A practical introduction to the profession — what the role actually involves, how private households operate, what principals expect, and how to begin positioning yourself for the work.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                {enrolledIds.includes(flagship.id) ? (
                  <button data-testid="featured-open" onClick={() => nav(`/learn/${flagship.id}`)} className="mt-10 inline-flex items-center gap-3 border border-ink text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors group">Go to the Course <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></button>
                ) : flagship.available === false ? (
                  <div className="mt-10 flex flex-col items-start gap-4">
                    <span data-testid="featured-soon" className="inline-flex items-center gap-3 border border-ink/25 text-ink-soft px-8 py-4 text-[11px] uppercase tracking-[0.2em] cursor-default select-none">Coming Soon</span>
                    <Link to={`/course/${flagship.id}`} data-testid="featured-details" className="text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink link-underline inline-flex items-center gap-2">Read about the course <ArrowRight size={13} /></Link>
                  </div>
                ) : (
                  <Link to={`/course/${flagship.id}`} data-testid="featured-explore" className="mt-10 inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors group">Explore the Course <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></Link>
                )}
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* The library */}
      <section id="collection" className="border-t border-line scroll-mt-24">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <Reveal><h2 className="font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">The Collection</h2></Reveal>
            <Reveal delay={0.08}>
              <div className="flex gap-6">
                {[{ k: "all", l: "All" }, ...CATS.map((c) => ({ k: c.key, l: c.label }))].map((t) => (
                  <button key={t.k} data-testid={`filter-${t.k}`} onClick={() => setFilter(t.k)} className={`text-[11px] uppercase tracking-[0.2em] transition-colors ${filter === t.k ? "text-ink border-b border-ink pb-1" : "text-ink-soft hover:text-ink pb-1"}`}>{t.l}</button>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="border-t border-line">
            {shown.length > 0 ? shown.map((it) => <ItemRow key={`${it.kind}-${it.id}`} it={it} />)
              : <p className="py-12 font-serif italic text-xl text-ink-soft">New pieces are being added to this shelf.</p>}
          </div>
        </div>
      </section>

      {/* Complimentary resource — email capture */}
      <section id="free" className="border-t border-line bg-ink text-bg scroll-mt-24">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent">Complimentary Resource</p>
            <h2 className="mt-6 font-serif font-light text-4xl lg:text-5xl leading-tight tracking-tight">Hurricane Preparedness Checklist</h2>
            <p className="mt-6 text-base font-light text-bg/70 leading-relaxed max-w-md">
              A practical resource for preparing a residence before severe weather — based on the systems and considerations used in professional Estate Management.
            </p>
          </div>

          {checklistDone ? (
            <div data-testid="checklist-success" className="lg:justify-self-end w-full max-w-md">
              <div className="w-12 h-12 border border-bg/40 flex items-center justify-center"><Check size={22} strokeWidth={1.5} /></div>
              <h3 className="mt-6 font-serif font-light text-3xl tracking-tight">It's on its way.</h3>
              <p className="mt-4 text-base font-light text-bg/70 leading-relaxed">Your checklist is downloading now. You've also joined <span className="text-bg">Notes from Il Salotto</span>.</p>
              <a href={downloadUrl} data-testid="success-redownload" className="mt-3 inline-block text-[11px] uppercase tracking-[0.2em] text-bg/70 hover:text-bg link-underline">Download again</a>
              <div className="mt-8 border-t border-bg/20 pt-8">
                <p className="text-[11px] uppercase tracking-ultra text-accent">While you're here</p>
                <button onClick={() => document.getElementById("collection")?.scrollIntoView({ behavior: "smooth", block: "start" })} data-testid="success-explore-collection" className="mt-4 inline-flex items-center gap-3 bg-bg text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors group">
                  Explore the collection <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={requestChecklist} data-testid="checklist-form" className="lg:justify-self-end w-full max-w-md space-y-6">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-bg/60">First Name</label>
                <input data-testid="checklist-firstname" value={checklist.first_name} onChange={(e) => setChecklist({ ...checklist, first_name: e.target.value })} className="mt-2 w-full bg-transparent border-b border-bg/30 focus:border-bg outline-none py-3 text-bg text-lg font-light transition-colors placeholder:text-bg/40" placeholder="Your first name" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-bg/60">Email</label>
                <input data-testid="checklist-email" type="email" value={checklist.email} onChange={(e) => setChecklist({ ...checklist, email: e.target.value })} className="mt-2 w-full bg-transparent border-b border-bg/30 focus:border-bg outline-none py-3 text-bg text-lg font-light transition-colors placeholder:text-bg/40" placeholder="you@email.com" />
              </div>
              <p className="text-xs font-light text-bg/50 leading-relaxed">You'll also receive <span className="text-bg/80">Notes from Il Salotto</span>. Unsubscribe anytime.</p>
              <button type="submit" disabled={sending} data-testid="checklist-submit" className="group w-full bg-bg text-ink py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors flex items-center justify-center gap-3 disabled:opacity-60">{sending ? "Sending…" : "Send Me the Checklist"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></button>
            </form>
          )}
        </div>
      </section>

      {/* Notes from Il Salotto */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
          <Reveal className="lg:col-span-4"><MetaLabel>The Letter</MetaLabel></Reveal>
          <Reveal delay={0.08} className="lg:col-span-8">
            <NewsletterSignup source="salotto" tone="light" copy={NOTES_COPY} />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
